import React, { useEffect } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { Box, CssBaseline } from "@mui/material";
import { useAuth } from "./hooks/useBCT";
import Header from "./components/Layout/header/Header";
import Footer from "./components/Layout/footer/Footer";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AnimatedLoginScreen from "./auth/AnimatedLoginScreen";
import LoginErrorScreen from "./auth/LoginErrorScreen";
import UnAuthorizedScreen from "./auth/UnAuthorizedScreen";
import "./i18n";
import AppRouter from "./Router";
import { BCTProvider } from "./context/BCTContext";
import { useBCT } from "./hooks/useBCT";
import GlobalModalContainer from "./components/customGenericComponents/GlobalModalContainer/GlobalModalContainer";

// Component to handle data initialization after authentication
const AuthenticatedApp: React.FC = () => {
  const { initializeData, isInitialized } = useBCT();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated && !isInitialized) {
      console.log('User authenticated, initializing data...');
      initializeData().catch(error => {
        console.error('Failed to initialize data after authentication:', error);
      });
    }
  }, [isAuthenticated, isInitialized, initializeData]);

  return (
    <Router>
      <CssBaseline />
      <Box sx={{ 
        display: "flex", 
        flexDirection: "column", 
        height: "100vh", 
        width: "100vw", 
        overflow: "hidden" 
      }}>
        <Header />
        <Box 
          component="main" 
          sx={{ 
            flex: 1, 
            overflow: "auto", 
            display: "flex", 
            flexDirection: "column" 
          }}
        >
          <AppRouter />
        </Box>
        <Footer />
        <ToastContainer 
          position="top-right" 
          autoClose={5000} 
          hideProgressBar={false} 
          newestOnTop={false} 
          closeOnClick 
          rtl={false} 
          pauseOnFocusLoss 
          draggable 
          pauseOnHover 
          toastStyle={{ fontSize: "clamp(0.875rem, 2vw, 1rem)" }} 
        />
        <GlobalModalContainer />
      </Box>
    </Router>
  );
};

const App: React.FC = () => {
  const { user, isAuthenticated, loading, loginError, retryLogin, unauthorized, stage } = useAuth();

  // Show loading during authentication with stage tracking
  if (loading) {
    return <AnimatedLoginScreen stage={stage} />;
  }

  // Show unauthorized screen if user is not in the system
  if (unauthorized && user) {
    return (
      <UnAuthorizedScreen 
        userName={user.name}
        userEmail={user.email}
      />
    );
  }

  // Show error screen if authentication failed (but not unauthorized)
  if (loginError && !unauthorized) {
    return <LoginErrorScreen error={loginError} onRetry={retryLogin} />;
  }

  // Show loading if not authenticated (prevents content flash)
  if (!isAuthenticated || !user) {
    return <AnimatedLoginScreen stage="finalizing" message="Preparing your workspace..." />;
  }

  return (
    <BCTProvider>
      <AuthenticatedApp />
    </BCTProvider>
  );
};

export default App;